import axios from 'axios';
import { currentAuthOwner, writeAuthCookies } from '@/shared/utils/authCookieOwner';
import type { AuthTokenService } from './AuthTokenService';
import type { CampaignBootstrapResponse } from '@/shared/utils/campaignBootstrap';

export interface CampaignBootstrapRedeemRequest {
  token: string;
}

export class CampaignBootstrapService {
  private readonly apiClient;

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly baseUrl: string
  ) {
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.apiClient.interceptors.request.use(
      (config) => {
        const url = typeof config.url === 'string' ? config.url : '';
        const isAuthEndpoint = url.startsWith('/v1/auth/');
        if (!isAuthEndpoint) {
          const token = this.authTokenService.getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async redeemCampaignBootstrap(
    request: CampaignBootstrapRedeemRequest
  ): Promise<CampaignBootstrapResponse> {
    return writeAuthCookies(currentAuthOwner(), async () => {
      const response = await this.apiClient.post('/v1/auth/campaign-bootstrap', {
        token: request.token,
      });

      const data = response.data as CampaignBootstrapResponse;

      if (data.status === 'existing_user') {
        return {
          status: 'existing_user',
          access_token: String(data.access_token),
          refresh_token: String(data.refresh_token),
          expires_in:
            typeof data.expires_in === 'number' ? data.expires_in : undefined,
          session_id:
            typeof data.session_id === 'string' ? data.session_id : undefined,
        };
      }

      return {
        status: 'new_user',
        bootstrap_session_id: String(data.bootstrap_session_id),
        phone: String(data.phone),
        expires_in: Number(data.expires_in ?? 0),
      };
    });
  }
}
